import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { RadioButton, RadioButtonGroup } from '@sunwebgroup/uds-ui/radio-button'
import * as React from 'react'
import { toSnakeCase } from '@/helpers/string-helpers'
import { useIntl } from '@/intl'
import {
  FilterContainer,
  FilterItemOption,
  FilterItemOptionContent,
  FilterItemOptionControl,
  FilterItemOptionTrailing,
  FilterItemOptionWrapper,
  FilterItemsContainer,
  FilterLabel,
  FilterOptionPrice,
  FilterOptionValue,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/Filters'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { updateSingleValueFilter } from '@/scopes/checkout/filterableCars/domain/actions'
import { useFilter } from '@/scopes/checkout/filterableCars/domain/hooks'
import { CarFilterType } from '@/scopes/checkout/filterableCars/domain/types'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

type CarClassFilterFilterType = 'all_cars' | 'small' | 'economy' | 'compact' | 'intermediate' | 'mini_van'

function filterTranslationTextKeys(key: CarClassFilterFilterType): string | undefined {
  const mapping: Record<string, string> = {
    all_cars: 'checkout_steps_ancillaries_car_rental_filtering_filters_car_class_type_all_cars_text',
    small: 'checkout_steps_ancillaries_car_rental_filtering_filters_car_class_type_small_text',
    economy: 'checkout_steps_ancillaries_car_rental_filtering_filters_car_class_type_economy_text',
    compact: 'checkout_steps_ancillaries_car_rental_filtering_filters_car_class_type_compact_text',
    intermediate: 'checkout_steps_ancillaries_car_rental_filtering_filters_car_class_type_intermediate_text',
    mini_van: 'checkout_steps_ancillaries_car_rental_filtering_filters_car_class_type_mini_van_text',
  }
  return mapping[key]
}

export function CarClassFilter() {
  const { filter, setFilterValue } = useFilter(CarFilterType.CarClass)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()

  const handleFilterValueChange = React.useCallback(
    (value: string) => {
      if (!value)
        return
      if (!filter)
        return
      setFilterValue(updateSingleValueFilter({ value })(filter))

      sendEvent(
        createCarEvent({
          result: 'Filter selected',
          type: value,
          description: 'Car class',
          element: 'radiobutton',
          validated: true,
        }),
      )
    },
    [sendEvent, filter, setFilterValue],
  )

  if (!filter)
    return null

  const { filterOptions, selectedFilter } = filter

  return (
    <FilterContainer>
      <FilterLabel>{intl.formatMessage({ id: `checkout_steps_ancillaries_car_rental_filtering_filters_car_class_type_label` })}</FilterLabel>
      <RadioButtonGroup value={selectedFilter ?? undefined} onValueChange={handleFilterValueChange}>
        <FilterItemsContainer>
          {filterOptions.map((option) => {
            const { value, name, quantity, startingPrice } = option
            const filterTextKey = filterTranslationTextKeys(toSnakeCase(name) as CarClassFilterFilterType) || ''

            return (
              <FilterItemOptionWrapper key={value}>
                <ListItem variant="outlined">
                  <FilterItemOption>
                    <FilterItemOptionControl>
                      <RadioButton value={value} />
                    </FilterItemOptionControl>
                    <FilterItemOptionContent>
                      <FilterOptionValue>
                        {filterTextKey ? intl.formatMessage({ id: filterTextKey }) : name}
                        {' '}
                        (
                        {quantity ?? 0}
                        )
                      </FilterOptionValue>
                    </FilterItemOptionContent>
                    {startingPrice !== undefined && startingPrice !== null && (quantity && quantity > 0)
                      ? (
                          <FilterItemOptionTrailing>
                            <FilterOptionPrice>
                              {intl.formatMessage({ id: `checkout_steps_ancillaries_car_rental_filtering_filters_from_starting_price_text` }, { price: intl.formatTruncatedCurrency(startingPrice) })}
                            </FilterOptionPrice>
                          </FilterItemOptionTrailing>
                        )
                      : ''}
                  </FilterItemOption>
                </ListItem>
              </FilterItemOptionWrapper>
            )
          })}
        </FilterItemsContainer>
      </RadioButtonGroup>
    </FilterContainer>
  )
}
